import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";
import PublicPageHelmet from "../../seo/PublicPageHelmet";

import Header from "./../../components/Header";

import PropertyCardList from "./components/PropertyCardList";

import SearchMap from "./components/SearchMap/SearchMap";

import TitleSearch from "./components/TitleSearch";

import {

  Wrapper,

  MapToggleListWrapper,

  DesktopDownHideWrapper,

  DesktopUpHideWrapper,

} from "./styles";

import { sortProperties } from "../../utils/sortProperties";

import PropertiesListSkeleton from "./components/Skeleton";

import FilterToggles from "./components/FilterToggles";

import { SortOptions } from "./enum";

import SearchFilter from "./components/TitleSearch/SearchFilter";

import { buildNeighborhoodContextUrl } from "./utils/neighborhoodDetection";

import {

  consumeReturnContext,

  isMapViewCompatibleWithCity,

  loadMapView,

  saveMapView,

  SavedMapView,

} from "./utils/mapViewStorage";

import { MapBounds } from "./utils/mapBoundsUtils";

import { resolveMapNavigatedProperties } from "./utils/resolveMapNavigatedProperties";

import { buildSearchFetchKey } from "./utils/searchFetchKey";

import { filterParentProperties } from "../../utils/propertyDisplay";

import { useCityProperties } from "../../hooks/useCityProperties";

import { usePropertyZoomReload } from "../../hooks/usePropertyZoomReload";

import { MapViewportPayload } from "./components/SearchMap/SearchMap";



function parseSearchParams(search: string) {

  const urlParams = new URLSearchParams(search);

  let uf = urlParams.get("UF");



  if (!uf) {

    uf = urlParams.get("uf");

  }



  if (uf) {

    if (uf.includes("?")) {

      uf = uf.split("?")[0];

    } else if (uf.includes("%3F")) {

      uf = uf.split("%3F")[0];

    }

  }



  return {

    condominium: urlParams.get("condominium"),

    uf: uf,

    city: urlParams.get("city"),

    neighborhood: urlParams.get("neighborhood"),

    type: urlParams.get("imovelType"),

    rentValue: urlParams.get("values"),

    area: urlParams.get("area"),

    rooms: urlParams.get("rooms"),

    garages: urlParams.get("garages"),

    bathrooms: urlParams.get("bathrooms"),

    furnished: urlParams.get("furnished"),

    total: urlParams.get("total"),

    has_sea_view: urlParams.get("has_sea_view"),

    has_pool: urlParams.get("has_pool"),

    has_ac: urlParams.get("has_ac"),

    has_porch: urlParams.get("has_porch"),

    has_storage: urlParams.get("has_storage"),

    has_elevator: urlParams.get("has_elevator"),

    has_gym: urlParams.get("has_gym"),

    has_sports: urlParams.get("has_sports"),

    has_playground: urlParams.get("has_playground"),

    has_bbq: urlParams.get("has_bbq"),

    has_party: urlParams.get("has_party"),

    has_game_room: urlParams.get("has_game_room"),

    has_garden: urlParams.get("has_garden"),

    has_24h_concierge: urlParams.get("has_24h_concierge"),

    minimal: 1,

  };

}



function buildCityApiParams(

  searchParams: ReturnType<typeof parseSearchParams>

): Record<string, unknown> {

  const {

    neighborhood: _neighborhood,

    city: _city,

    uf: _uf,

    ...apiParams

  } = searchParams;



  return Object.fromEntries(

    Object.entries(apiParams).filter(

      ([, value]) => value !== null && value !== undefined

    )

  );

}



export default function PropertiesList() {

  const history = useHistory();

  const location = useLocation();



  const [openFiltersModal, setOpenFiltersModal] = useState(false);

  const [sortPropertiesBy, setSortPropertiesBy] = useState<SortOptions>(

    SortOptions.NO_PREFERENCE

  );

  const [showMap, setShowMap] = useState(true);

  const [isEmptyArea, setIsEmptyArea] = useState(false);

  const [originalCity, setOriginalCity] = useState<string | null>(null);

  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);

  const [restoredMapView, setRestoredMapView] = useState<SavedMapView | null>(

    null

  );

  const [preferRestoredView, setPreferRestoredView] = useState(false);

  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);

  const [useViewportFilter, setUseViewportFilter] = useState(false);



  const {

    registerPropertyClick,

    consumeZoomReload,

    resetZoomReload,

  } = usePropertyZoomReload();



  const searchParams = useMemo(

    () => parseSearchParams(location.search),

    [location.search]

  );



  const cityApiParams = useMemo(

    () => buildCityApiParams(searchParams),

    [location.search]

  );



  const {

    allProperties: fetchedProperties,

    isLoading: loading,

    error: fetchError,

    refetch,

  } = useCityProperties({

    city: searchParams.city,

    uf: searchParams.uf,

    apiParams: cityApiParams,

    enabled: !!searchParams.city,

  });

  const parentProperties = useMemo(
    () => filterParentProperties(fetchedProperties),
    [fetchedProperties]
  );

  const searchFetchKey = buildSearchFetchKey(
    searchParams.city,
    searchParams.uf,
    JSON.stringify(cityApiParams)
  );

  const [isSearchPending, setIsSearchPending] = useState(
    () => !!searchParams.city
  );

  // Only gate the page skeleton on params that actually trigger a city fetch.
  // Neighborhood / map view URL churn must not remount the page or the map.
  useLayoutEffect(() => {
    if (searchParams.city) {
      setIsSearchPending(true);
    }
  }, [searchFetchKey, searchParams.city]);

  useEffect(() => {
    if (!loading) {
      setIsSearchPending(false);
    }
  }, [loading]);

  const isPageLoading = loading || isSearchPending;



  useEffect(() => {

    setSelectedPropertyId(null);



    if (history.action === "POP") {

      const returnView = consumeReturnContext() ?? loadMapView();

      setRestoredMapView(returnView);

      setPreferRestoredView(!!returnView);

    } else {

      setRestoredMapView(null);

      setPreferRestoredView(false);

    }

    // New search-bar / filter navigation (PUSH) resets viewport filtering so
    // neighborhood deep-links and search results apply before the next map gesture.
    if (history.action === "PUSH") {
      setMapBounds(null);
      setUseViewportFilter(false);
    }

  }, [location.key, history.action]);



  useEffect(() => {

    if (searchParams.city) {

      setOriginalCity(searchParams.city);

    }

  }, [searchParams.city]);



  const listResolution = useMemo(

    () =>

      resolveMapNavigatedProperties(parentProperties, {
        neighborhood: searchParams.neighborhood,
        mapBounds,
        useViewportFilter,
      }),

    [parentProperties, searchParams.neighborhood, mapBounds, useViewportFilter]

  );

  const highlightedPropertyIds = useMemo(() => {
    if (listResolution.isViewportFiltered || listResolution.appliedNeighborhood) {
      return listResolution.properties.map((property) => property.id);
    }

    // fellBackToCity or unfiltered city list: keep all pins default blue
    return null;
  }, [
    listResolution.isViewportFiltered,
    listResolution.appliedNeighborhood,
    listResolution.properties,
  ]);



  const propertiesToDisplay = useMemo(() => {
    let baseProperties = listResolution.properties;

    if (selectedPropertyId !== null) {
      const selectedProperty = parentProperties.find(
        (property) => property.id === selectedPropertyId
      );
      baseProperties = selectedProperty ? [selectedProperty] : [];
    }

    return sortProperties(
      baseProperties,
      listResolution.appliedNeighborhood,
      searchParams.condominium
    );
  }, [
    parentProperties,
    listResolution.properties,
    listResolution.appliedNeighborhood,
    searchParams.condominium,
    selectedPropertyId,
  ]);



  useEffect(() => {

    if (!isPageLoading) {

      setIsEmptyArea(useViewportFilter && propertiesToDisplay.length === 0);

    }

  }, [isPageLoading, propertiesToDisplay.length, useViewportFilter]);



  const getFiltersQuantity = () => {

    const urlParams = new URLSearchParams(location.search);

    const filtersArray = [

      urlParams.get("imovelType"),

      urlParams.get("values"),

      urlParams.get("area"),

      urlParams.get("rooms"),

      urlParams.get("garages"),

      urlParams.get("bathrooms"),

      urlParams.get("furnished"),

      urlParams.get("has_sea_view"),

      urlParams.get("has_pool"),

      urlParams.get("has_ac"),

      urlParams.get("has_porch"),

      urlParams.get("has_storage"),

      urlParams.get("has_elevator"),

      urlParams.get("has_gym"),

      urlParams.get("has_sports"),

      urlParams.get("has_playground"),

      urlParams.get("has_bbq"),

      urlParams.get("has_party"),

      urlParams.get("has_game_room"),

      urlParams.get("has_garden"),

      urlParams.get("has_24h_concierge"),

    ];

    let filtersQuantity = 0;



    for (const filter in filtersArray) {

      if (filtersArray[filter]) filtersQuantity++;

    }



    return filtersQuantity;

  };



  const updateUrlNeighborhood = useCallback(

    (neighborhood: string | null) => {

      const currentNeighborhood = new URLSearchParams(location.search).get(

        "neighborhood"

      );



      if (currentNeighborhood === neighborhood) {

        return;

      }



      if (!neighborhood && !currentNeighborhood) {

        return;

      }



      const nextUrl = buildNeighborhoodContextUrl(

        neighborhood,

        location.search

      );



      history.replace(nextUrl);
    },

    [history, location.search]

  );



  const handleMapNeighborhoodChange = useCallback(

    (neighborhood: string | null) => {

      resetZoomReload();

      updateUrlNeighborhood(neighborhood);

    },

    [resetZoomReload, updateUrlNeighborhood]

  );



  const handleMapViewChange = useCallback((view: SavedMapView) => {

    saveMapView({
      ...view,
      city: searchParams.city,
    });

  }, [searchParams.city]);



  const handleViewportChange = useCallback((viewport: MapViewportPayload) => {
    setMapBounds(viewport.bounds);
    setUseViewportFilter(true);
  }, []);



  const handleMapReady = useCallback(() => {}, []);



  const handleZoomEnd = useCallback(
    (zoom: number) => {
      if (!consumeZoomReload(zoom)) {
        return;
      }

      setSelectedPropertyId(null);
    },
    [consumeZoomReload]
  );



  function handleToggleMap() {

    setShowMap((prevValue) => !prevValue);

  }



  function handleCloseFiltersModal() {

    setOpenFiltersModal(false);

  }



  function handleOpenFiltersModal() {

    setOpenFiltersModal(true);

  }



  function handleSortPropertiesByPrice(orderBy: SortOptions) {

    setSortPropertiesBy(orderBy);

  }



  const handleMapMarkerSelect = useCallback(
    (propertyId: number, mapZoom?: number) => {
      setSelectedPropertyId(propertyId);
      setIsEmptyArea(false);

      registerPropertyClick(
        {
          neighborhood: searchParams.neighborhood,
          city: searchParams.city,
          uf: searchParams.uf,
        },
        mapZoom
      );
    },
    [
      registerPropertyClick,
      searchParams.city,
      searchParams.neighborhood,
      searchParams.uf,
    ]
  );



  const handleReloadPage = useCallback(() => {

    refetch();

    setIsEmptyArea(false);

  }, [refetch]);



  const sortedPropertiesList =

    sortPropertiesBy !== SortOptions.NO_PREFERENCE

      ? sortProperties(

          propertiesToDisplay,

          undefined,

          undefined,

          sortPropertiesBy

        )

      : propertiesToDisplay;



  const filtersQuantity = getFiltersQuantity();

  const initialMapView = useMemo(() => {
    const candidate = restoredMapView ?? loadMapView();
    if (!isMapViewCompatibleWithCity(candidate, searchParams.city)) {
      return null;
    }

    return candidate;
  }, [restoredMapView, searchParams.city]);



  if (isPageLoading) {

    return (

      <>

        <PublicPageHelmet pathname={location.pathname} search={location.search} />

      <Header hasBorder isToShowSearchBarMobile />

        <SearchFilter

          open={openFiltersModal}

          onClose={handleCloseFiltersModal}

          isLoadingApiRequest={loading}

          onSortProperty={handleSortPropertiesByPrice}

        />

        <PropertiesListSkeleton />

      </>

    );

  }



  return (

    <>

      <PublicPageHelmet pathname={location.pathname} search={location.search} />

      <Header hasBorder isToShowSearchBarMobile />

      <SearchFilter

        open={openFiltersModal}

        onClose={handleCloseFiltersModal}

        isLoadingApiRequest={loading}

        onSortProperty={handleSortPropertiesByPrice}

      />

      <Wrapper>

        <TitleSearch

          numberOfProperties={propertiesToDisplay.length}

          hasSelectedProperty={selectedPropertyId !== null}

          onFiltersButtonClick={handleOpenFiltersModal}

          filtersQuantity={filtersQuantity || undefined}

          city={searchParams.city ?? undefined}

          uf={searchParams.uf ?? undefined}

          neighborhood={listResolution.appliedNeighborhood ?? undefined}

          searchedNeighborhood={searchParams.neighborhood ?? undefined}

          fellBackToCity={listResolution.fellBackToCity}

        />



        <DesktopDownHideWrapper>

          <FilterToggles

            isMapVisible={showMap}

            onToggleMap={handleToggleMap}

            onSortPropertiesByPrice={handleSortPropertiesByPrice}

            defaultSelectValue={sortPropertiesBy}

          />

        </DesktopDownHideWrapper>

        <MapToggleListWrapper className="property-listing-page">

          <SearchMap

            isMapVisible={showMap}

            properties={parentProperties}

            mapCenterNeighborhood={
              listResolution.fellBackToCity
                ? null
                : searchParams.neighborhood
            }

            city={searchParams.city}

            uf={searchParams.uf}

            initialMapView={initialMapView}

            preferRestoredView={preferRestoredView}

            highlightedPropertyIds={highlightedPropertyIds}

            onNeighborhoodUrlChange={handleMapNeighborhoodChange}

            onMapViewChange={handleMapViewChange}

            onViewportChange={handleViewportChange}

            onMapReady={handleMapReady}

            onMarkerSelect={handleMapMarkerSelect}

            onZoomEnd={handleZoomEnd}

          />

          <DesktopUpHideWrapper>

            <FilterToggles

              isMapVisible={showMap}

              onToggleMap={handleToggleMap}

              onOpenFiltersModal={handleOpenFiltersModal}

              defaultSelectValue={sortPropertiesBy}

              filtersQuantity={filtersQuantity || undefined}

            />

          </DesktopUpHideWrapper>



          <PropertyCardList

            propertiesList={sortedPropertiesList}

            isMapActive={showMap}

            isLoading={isPageLoading}

            searchParams={location.search}

            error={fetchError}

            neighborhoodFilter={listResolution.appliedNeighborhood}

            isEmptyArea={isEmptyArea}

            originalCity={originalCity}

            onReloadPage={handleReloadPage}

            onCloseMessage={() => setIsEmptyArea(false)}

          />

        </MapToggleListWrapper>

      </Wrapper>

    </>

  );

}


